import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Row, Col, Label } from "reactstrap";
import { Link } from "react-router-dom";
import DashboardHeader from "../includes/dashboardHeader/DashboardHeader";
import DashboardSidebar from "../includes/dashboardSidebar/DashboardSidebar";
import FooterComponent from "../includes/dashboardFooter/FooterComponent";
import AsideComponent from "../includes/asideComponent/AsideComponent";
import { startLoader, stopLoader } from "../../actions/loaderActions";
import { responseMessage } from "../../utils/alert";
import ApiServer from "./../../common/js/ApiServices.js";

// Charts
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams } from "react-router-dom";


// ---- helpers ----
const fmtScore = (v) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(2));
const avg = (arr) => {
  const vals = (arr || []).filter((x) => x != null && isFinite(x));
  if (!vals.length) return null;
  const s = vals.reduce((a, b) => a + b, 0);
  return +(s / vals.length).toFixed(2);
};


function ReEstablishAfly({ startLoader, stopLoader }) {
  const api = useMemo(() => new ApiServer(true), []);
  const { projId, projName, decompId } = useParams();
  const bootDecompId = Number(decompId) || 0;

  const [selectedDecompId, setSelectedDecompId] = useState(bootDecompId);
  // selections
  const [projects, setProjects] = useState([]); // capability modelling projects
  const [selectedDecompName, setSelectedDecompName] = useState("");
  const [bm, setBm] = useState({ id: 0, name: "" }); // linked benchmarking project

  // raw data
  const [domains, setDomains] = useState([]); // /get_user_domains_bm_project/:bmId
  const [capabilities, setCapabilities] = useState([]); // /capability-mapping-modeling-project-capability/:decompId
  const [mappingPairs, setMappingPairs] = useState([]); // ["capId|domainId"]

  // meta
  const [latestVersion, setLatestVersion] = useState(0);
  const [capabilityMappingId, setCapabilityMappingId] = useState(0);

  // === API calls ===
  // NOTE: define in a safe order so dependencies exist before use
  const fetchProjects = useCallback(async () => {
    startLoader();
    try {
      const res = await api.SendRequest({ method: "GET", url: "/capability-mapping-modeling-project" });
      if (res?.success && Array.isArray(res.data)) setProjects(res.data);
    } catch (e) {
      console.error(e);
      responseMessage("error", "Failed to load projects", "");
    } finally {
      stopLoader();
    }
  }, [api, startLoader, stopLoader]);

  const fetchDomains = useCallback(async (bmId) => {
    try {
      const res = await api.SendRequest({ method: "GET", url: `/get_user_domains_bm_project/${bmId}` });
      if (res?.success && Array.isArray(res.data)) setDomains(res.data);
      else setDomains([]);
    } catch (e) {
      console.error(e);
      setDomains([]);
      responseMessage("error", "Failed to load domains", "");
    }
  }, [api]);

  const fetchCapabilities = useCallback(async (decompId) => {
    try {
      const res = await api.SendRequest({ method: "GET", url: `/capability-mapping-modeling-project-capability/${decompId}` });
      if (res?.success && Array.isArray(res.data)) setCapabilities(res.data);
      else setCapabilities([]);
    } catch (e) {
      console.error(e);
      setCapabilities([]);
      responseMessage("error", "Failed to load capabilities", "");
    }
  }, [api]);

  const fetchMapping = useCallback(async (capabilityMappingID, bmId, decompId) => {
    try {
      const res = await api.SendRequest({ method: "GET", url: `/capability-mapping/${capabilityMappingID}/${bmId}/${decompId}` });
      if (res?.success && res.data?.mapping) {
        const m = [];
        for (let i = 0; i < res.data.mapping.length; i++) {
          const el = res.data.mapping[i];
          m.push(`${el.DecompositionProcessLevel1ID}|${el.DomainID}`);
        }
        setMappingPairs(m);
        setCapabilityMappingId(res.data[0]?.CapabilityMappingID || 0);
        setLatestVersion(
          res.data[0]?.Version == 0
            ? (parseInt(res.data[0]?.basedOnVersionID, 10) || 0)
            : (res.data[0]?.Version || 0)
        );
      } else {
        setMappingPairs([]);
      }
    } catch (e) {
      console.error(e);
      setMappingPairs([]);
      responseMessage("error", "Failed to load mapping", "");
    }
  }, [api]);

  const fetchLinkedBenchmark = useCallback(async (decompId) => {
    startLoader();
    try {
      const res = await api.SendRequest({ method: "GET", url: `/capability-mapping-linked-benchmark-project/${decompId}` });
      if (!res?.success || !res.data?.BenchmarkProjectID) {
        responseMessage("error", "Project not linked with Benchmarking project", "");
        return;
      }
      const projectName = (projects || []).find((p) => p.DecompositionProjectID == decompId)?.ProjectName || "";
      setSelectedDecompName(projectName);
      setBm({ id: res.data.BenchmarkProjectID, name: res.data.BenchmarkProjectName });

      await Promise.all([
        fetchDomains(res.data.BenchmarkProjectID),
        fetchCapabilities(decompId),
        fetchMapping(0, res.data.BenchmarkProjectID, decompId),
      ]);
    } catch (e) {
      console.error(e);
      responseMessage("error", "Failed to link benchmark project", "");
    } finally {
      stopLoader();
    }
  }, [api, projects, startLoader, stopLoader, fetchDomains, fetchCapabilities, fetchMapping]);

  // initial
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // on project select
  useEffect(() => {
    if (!selectedDecompId) return;

    setBm({ id: 0, name: "" });
    setDomains([]);
    setCapabilities([]);
    setMappingPairs([]);
    setLatestVersion(0);
    setCapabilityMappingId(0);

    // This continues to fetch domains/capabilities/mapping via the linked BM project
    fetchLinkedBenchmark(selectedDecompId);
  }, [selectedDecompId, fetchLinkedBenchmark]);

  // === Compute capability averages per domain (same as original logic) ===
  const scoreByCapId = useMemo(() => {
    const map = new Map();
    for (let i = 0; i < capabilities.length; i++) {
      const c = capabilities[i];
      const raw = c.CapabilityScore != null ? c.CapabilityScore : (c.AggrScore != null ? c.AggrScore : (c.Score != null ? c.Score : null));
      const num = raw == null ? null : Number(raw);
      map.set(String(c.DecompositionProcessLevel1ID), Number.isFinite(num) ? num : null);
    }
    return map;
  }, [capabilities]);

  const domainAvgById = useMemo(() => {
    const buckets = {}; // DomainID -> number[]
    for (let i = 0; i < mappingPairs.length; i++) {
      const pair = String(mappingPairs[i]);
      const parts = pair.split("|");
      if (parts.length !== 2) continue;
      const capId = String(parts[0]);
      const domId = String(parts[1]);
      const s = scoreByCapId.get(capId);
      if (s == null) continue;
      if (!buckets[domId]) {
        buckets[domId] = [];
      }
      buckets[domId].push(s);
    }
    const avgs = {};
    for (let i = 0; i < domains.length; i++) {
      const d = domains[i];
      const key = String(d.DomainID);
      const arr = buckets[key] || [];
      avgs[d.DomainID] = arr.length ? +(arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2) : null;
    }
    return avgs;
  }, [mappingPairs, scoreByCapId, domains]);

  // === Enrich domains with avg and net deviation (%) ===
  // Net Deviation (%) = ((my_score - avg_capability_score) / avg_capability_score) * 100
  const enrichedDomains = useMemo(() => {
    return (domains || []).map((d) => {
      const avgCap = domainAvgById[d.DomainID];
      const myRaw = d.my_score;
      const avgScore = avgCap == null ? null : Number(avgCap);
      const myScore = myRaw == null ? null : Number(myRaw);
      let netDeviationPct = null;
      if (avgScore != null && isFinite(avgScore) && avgScore !== 0 && myScore != null && isFinite(myScore)) {
        netDeviationPct = +(((myScore - avgScore) / avgScore) * 100).toFixed(2);
      }
      return {
        ...d,
        avg_capability_score: avgScore,
        my_score: myScore,
        net_deviation_pct: netDeviationPct,
      };
    });
  }, [domains, domainAvgById]);

  // === Aggregates for summary table ===
  const overallAmpMarking = useMemo(() => avg(enrichedDomains.map((d) => d.my_score)), [enrichedDomains]);
  const overallCapability = useMemo(() => avg(enrichedDomains.map((d) => d.avg_capability_score)), [enrichedDomains]);
  const overallNetDeviation = useMemo(() => {
    if (overallAmpMarking == null || overallCapability == null) return null;
    return +(overallAmpMarking - overallCapability).toFixed(2);
  }, [overallAmpMarking, overallCapability]);
  const reEstablishedAfly = useMemo(() => {
    if (overallAmpMarking == null || overallCapability == null) return null;
    return +(((overallAmpMarking + overallCapability) / 2)).toFixed(2);
  }, [overallAmpMarking, overallCapability]);

  // === Chart data ===
  const chart1 = useMemo(() => {
    return enrichedDomains.map((d) => ({
      name: d.DomainName,
      AmpMarking: d.my_score == null ? 0 : Number(d.my_score),
      CapabilityModeling: d.avg_capability_score == null ? 0 : Number(d.avg_capability_score),
    }));
  }, [enrichedDomains]);

  const chart2 = useMemo(() => {
    return enrichedDomains.map((d) => ({
      name: d.DomainName,
      NetDeviationPct: d.net_deviation_pct == null ? 0 : Number(d.net_deviation_pct),
    }));
  }, [enrichedDomains]);
  return (
    <>
      <DashboardHeader />
      <div id="wrapper">
        <DashboardSidebar />
        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <ol className="breadcrumb dashbread">
              <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
              <li className="breadcrumb-item active">Capability Mapping — Analytics</li>
              <li className="breadcrumb-menu d-md-down-none ml-auto">
                <img src={require("./../../common/images/diva-icon.png")} className="logo-img" alt="Logo" />
                <a className="btn powered p-0" href="#"><i className="icon-graph"></i>&nbsp;<i>Powered by Amploglobal</i></a>
              </li>
            </ol>

            <div className="container-fluid">
              {/* Report Title */}
              <Row className="mb-2">
                <Col xl="12">
                  <h4
                    style={{
                      textAlign: "center",
                      fontWeight: 800,
                      margin: 0,
                      WebkitBackgroundClip: "text",
                      color: "#3045d3"
                    }}
                  >
                    Report for Re-Establishing AFLY Score
                  </h4>
                </Col>
              </Row>

              {/* Top Right Project Labels */}
              <Row>
                <Col xl="4">
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 12,
                      background: "linear-gradient(135deg,#ffffff 0%,#f0f9ff 60%,#ecfeff 100%)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                      fontSize: 12,
                      color: "#0f172a",
                    }}
                  >
                    {bm?.name && (
                      <div style={{ marginBottom: 4 }}>
                        <strong style={{ color: "#6366f1" }}>AmpMarking Project:</strong> {bm.name}
                      </div>
                    )}
                    {selectedDecompName && (
                      <div>
                        <strong style={{ color: "#22c55e" }}>Capability Project:</strong> {selectedDecompName}
                      </div>
                    )}
                    {!bm?.name && !selectedDecompName && (
                      <div style={{ color: "#6b7280" }}>— No project info available —</div>
                    )}
                  </div>
                </Col>
                <Col xl="8" />
              </Row>

              {/* Content: either charts+summary OR empty state */}
              {selectedDecompId ? (
                <>
                  {/* Charts Row */}
                  <Row className="mt-2">
                    {/* Left: Domain Scores */}
                    <Col xl="6" className="mb-4">
                      <div
                        style={{
                          height: 340,
                          border: "1px solid #e5e7eb",
                          borderRadius: 16,
                          padding: 12,
                          background: "linear-gradient(135deg,#fff7ed 0%,#fffbeb 100%)",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div style={{ textAlign: "center", fontWeight: 800, marginBottom: 8, color: "#ea580c" }}>
                          Domain Scores
                        </div>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chart1} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="gradAmp" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6366f1" />
                                <stop offset="100%" stopColor="#60a5fa" />
                              </linearGradient>
                              <linearGradient id="gradCap" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#22c55e" />
                                <stop offset="100%" stopColor="#34d399" />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#fecaca" />
                            <XAxis
                              dataKey="name"
                              interval={0}
                              angle={0}
                              textAnchor="middle"
                              height={40}
                              tick={{ fill: "#0f172a", fontSize: 11 }}
                              stroke="#f97316"
                            />
                            <YAxis domain={[0, 5]} tick={{ fill: "#0f172a" }} stroke="#f97316" />
                            <Tooltip
                              formatter={(v) => fmtScore(v)}
                              contentStyle={{ borderRadius: 10, border: "1px solid #f59e0b" }}
                              wrapperStyle={{ outline: "none" }}
                            />
                            <Legend
                              iconType="circle"
                              wrapperStyle={{ paddingTop: 12, color: "#0f172a", fontWeight: 600 }}
                            />
                            <Bar dataKey="AmpMarking" name="AmpMarking" fill="url(#gradAmp)" radius={[6, 6, 0, 0]} />
                            <Bar dataKey="CapabilityModeling" name="Capability Modeling" fill="url(#gradCap)" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Col>

                    {/* Right: Net Deviation (%) */}
                    <Col xl="6" className="mb-4">
                      <div
                        style={{
                          height: 340,
                          border: "1px solid #e5e7eb",
                          borderRadius: 16,
                          padding: 12,
                          background: "linear-gradient(135deg,#eef2ff 0%,#f5f3ff 100%)",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                        }}
                      >
                        <div style={{ textAlign: "center", fontWeight: 800, marginBottom: 8, color: "#7c3aed" }}>
                          Net Deviation (%)
                        </div>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chart2} layout="vertical" margin={{ top: 10, right: 10, left: 20, bottom: 0 }}>
                            <defs>
                              <linearGradient id="gradDev" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#fb7185" />
                                <stop offset="100%" stopColor="#f43f5e" />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9d5ff" />
                            <XAxis type="number" tick={{ fill: "#0f172a" }} stroke="#a78bfa" />
                            <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#0f172a" }} stroke="#a78bfa" />
                            <Tooltip
                              formatter={(v) => `${fmtScore(v)}%`}
                              contentStyle={{ borderRadius: 10, border: "1px solid #8b5cf6" }}
                              wrapperStyle={{ outline: "none" }}
                            />
                            <Legend
                              iconType="circle"
                              wrapperStyle={{ paddingTop: 12, color: "#0f172a", fontWeight: 600 }}
                            />
                            <Bar dataKey="NetDeviationPct" name="Net Deviation (%)" fill="url(#gradDev)" radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Col>
                  </Row>

                  {/* Summary Table */}
                  <Row className="mt-2">
                    <Col xl="12" className="mb-5" style={{ display: "flex", justifyContent: "center" }}>
                      <div style={{ minWidth: 520, borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 16px rgba(0,0,0,0.06)" }}>
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "18px",
                            fontWeight: 800,
                            borderWidth: "6px",
                            borderTopStyle: "solid",
                            borderRightStyle: "solid",
                            borderBottomStyle: "initial",
                            borderLeftStyle: "solid",
                            borderTopColor: "rgba(0, 0, 0, 0.4)",
                            borderRightColor: "rgba(0, 0, 0, 0.46)",
                            borderBottomColor: "initial",
                            borderLeftColor: "rgba(0, 0, 0, 0.54)",
                            borderImage: "initial",
                            padding: "12px",
                            background: "linear-gradient(90deg, rgba(203, 209, 211, 0.75), rgba(174, 174, 174, 0.53))",
                            color: "rgb(20, 20, 20)",
                          }}
                        >
                          Re-established Afly Score
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, sans-serif" }}>
                          <tbody>
                            <tr style={{ background: "#f0f9ff" }}>
                              <td style={{ border: "1px solid #ddd", padding: "10px", width: "70%", fontWeight: 500 }}>
                                AmpMarking
                              </td>
                              <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "right", color: "#2563eb", fontWeight: 600 }}>
                                {fmtScore(overallAmpMarking)}
                              </td>
                            </tr>

                            <tr style={{ background: "#f9f5ff" }}>
                              <td style={{ border: "1px solid #ddd", padding: "10px", fontWeight: 500 }}>
                                Capability Modeling
                              </td>
                              <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "right", color: "#16a34a", fontWeight: 600 }}>
                                {fmtScore(overallCapability)}
                              </td>
                            </tr>

                            <tr style={{ background: "#fff7f0" }}>
                              <td style={{ border: "1px solid #ddd", padding: "10px", fontWeight: 500 }}>
                                Net Deviation
                              </td>
                              <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "right", color: "#db2777", fontWeight: 600 }}>
                                {fmtScore(overallNetDeviation)}
                              </td>
                            </tr>

                            <tr style={{ background: "#f3f4f6" }}>
                              <td style={{ border: "1px solid #ddd", padding: "10px", fontWeight: 700, color: "#111827" }}>
                                Re-established AFly Score
                              </td>
                              <td style={{ border: "1px solid #ddd", padding: "10px", textAlign: "right", fontWeight: 700, color: "#111827" }}>
                                {fmtScore(reEstablishedAfly)}
                              </td>
                            </tr>
                          </tbody>
                        </table>

                      </div>
                    </Col>
                  </Row>
                </>
              ) : (
                <Row className="mt-3">
                  <Col xl="12">
                    <div
                      style={{
                        border: "2px dashed #a3a3a3",
                        borderRadius: 16,
                        padding: 28,
                        background: "linear-gradient(135deg,#fafafa 0%,#f5f5f5 100%)",
                        textAlign: "center",
                        color: "#6b7280",
                        boxShadow: "inset 0 0 0 9999px rgba(255,255,255,0.35)",
                      }}
                    >
                      <div style={{ fontWeight: 800, marginBottom: 6, color: "#374151" }}>No Data to Display</div>
                      <div>
                        This report needs a linked Capability Modeling project (DecompositionProjectID). Open “Goal Setting” and link/select a project, then click “View Score.”
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          </div>
        </div>
      </div>
      <AsideComponent />
      <FooterComponent />
    </>
  );


}

export default connect(null, { startLoader, stopLoader })(ReEstablishAfly);
